import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ValidatorFn, Validators } from '@angular/forms';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { Router, ActivatedRoute } from '@angular/router';
import { LikutBarcodeDefinition, LikutProductBarcode, LikutProductBarcodePayload } from '@interfaces/likut';
import { ResponseFromExpress, SQLError, SQLResponse } from '@interfaces/SQL-interfaces';
import { AllProductsLikut } from '@interfaces/SQL-morning-responses';
import { BarcodeService } from '@services/barcode.service';
import { BottomPopupService } from '@services/bottom-popup.service';
import { generateDummyBarcodeFromDefinition, LikutScanService } from '@services/likut-scan.service';
import { LoadingService } from '@services/loading.service';
import { ProductsService } from '@services/products.service';
import { StoreService } from '@services/store.service';
import { TextService } from '@services/text.service';
import { MAXIMUM_PACK_QUANTITY } from '@utils/constants-products';
import { displayFn } from '@utils/display_functions/displayFn';
import { genericSearchingFunctionWithoutNullChecks as _filter } from '@utils/functions';
import { decompress } from 'lz-string';
import { BehaviorSubject, Subject, map, combineLatest, Observable, startWith, takeUntil, take, merge } from 'rxjs';
import { environment } from 'src/environments/environment';

const formDefaultValue = {
	productID: '',
	idFromBarcode: '',
	barcodeDefId: null,
	quantity: null,
	packQuantity: null,
};

type LikutProductBarcodeDisplay = LikutProductBarcode & { BarcodeName: string };

@Component({
	selector: 'app-likut-barcode-products-management',
	templateUrl: './likut-barcode-products-management.component.html',
	styleUrls: ['./likut-barcode-products-management.component.scss'],
})
export class LikutBarcodeProductsManagementComponent implements OnInit, OnDestroy {
	_text = inject(TextService);
	private _ss = inject(StoreService);
	private router = inject(Router);
	private fb = inject(FormBuilder);
	private _products = inject(ProductsService);
	private _loading = inject(LoadingService);
	private _bottomPopup = inject(BottomPopupService);
	private _likutScan = inject(LikutScanService);
	private _barcode = inject(BarcodeService);
	constructor() {}
	form = this.fb.nonNullable.group(
		{
			productID: ['', Validators.required],
			idFromBarcode: ['', [Validators.required]],
			barcodeDefId: [<string>null, [Validators.required, this.validateBarcodeDefinition.bind(this)]],
			quantity: [<string>null, [Validators.min(0), Validators.max(MAXIMUM_PACK_QUANTITY), Validators.pattern(`^ *[0-9]+(.[0-9]{0,3})? *$`)]],
			packQuantity: [<string>null, [Validators.min(0), Validators.max(MAXIMUM_PACK_QUANTITY), Validators.pattern(`^ *[0-9]+(.[0-9]{0,3})? *$`)]],
		},
		{ validators: [this.validateBarcodeDefinition.bind(this), this.validateDuplicateProduct.bind(this), this.validateBarcodeFromProduct.bind(this)] },
		// { validators: [this.validateBarcodeDefinition(), this.validateDuplicateProduct(), this.validateBarcodeFromProduct()] },
	);

	searchFormControl = new FormControl('', { nonNullable: true });
	searchFormControl$ = this.searchFormControl.valueChanges.pipe(
		startWith(''),
		map(value => value.trim()),
	);

	products$ = new BehaviorSubject<LikutProductBarcode[]>([]);
	readonly definitions$ = new BehaviorSubject<LikutBarcodeDefinition[]>([]);
	// definitionsDisplay$ = this.form.controls.barcodeDefId.valueChanges.pipe(
	// 	startWith(''),
	// 	map(value => _filter(value || '', this.definitions$.value, ['BarcodeName', 'Id'], 200)),
	// );
	productsDisplay$: Observable<LikutProductBarcodeDisplay[]> = combineLatest([this.products$, this.definitions$, this.searchFormControl$]).pipe(
		map(([products, definitions, search]) => {
			let productsAsDisplay: LikutProductBarcodeDisplay[] = products.map(product => {
				const definitionName = definitions.find(def => def.Id === product.BarcodeDefId);
				const displayProduct = product as LikutProductBarcodeDisplay;
				displayProduct.BarcodeName = definitionName?.BarcodeName || '';
				return displayProduct;
			});

			if (search) {
				productsAsDisplay = productsAsDisplay.filter(product => product.ProductId.includes(search));
			}

			return productsAsDisplay;
		}),
	);
	// productsDisplay$: Observable<LikutProductBarcodeDisplay[]> = combineLatest([this.products$, this.definitions$]).pipe(
	// 	map(([products, definitions]) => {
	// 		return products.map(product => {
	// 			const definitionName = definitions.find(def => def.Id === product.BarcodeDefId);
	// 			const displayProduct = product as LikutProductBarcodeDisplay;
	// 			displayProduct.BarcodeName = definitionName?.BarcodeName || '';
	// 			return displayProduct;
	// 		});
	// 	}),
	// );
	totalShown$ = this.productsDisplay$.pipe(map(arr => arr.length));
	productsFromMemory$ = new BehaviorSubject<AllProductsLikut[]>([]);
	productsFromMemoryDictionary$ = this.productsFromMemory$.pipe(map(products => products.reduce((acc, curr) => ({ ...acc, [curr.ProductId]: curr }), {})));

	loading = false;

	updateSuccess = false;

	destroy$: Subject<void> = new Subject();

	isEdittingBarcodeProduct = false;
	selectedBarcodeProductID = null;

	isLoadingProgressBarShowing = false;

	productBarcodeDrawerOpen = false;

	isDevelopment = environment.development;

	ngOnInit() {
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////
		if (this._ss.CustomerInformation === null) {
			const CustomerInformation = localStorage.getItem('CustomerInformation');
			if (!CustomerInformation) {
				this.router.navigate(['login']);
				return;
			}

			this._ss.CustomerInformation = JSON.parse(decompress(CustomerInformation));
		}
		//////////////////// ---------- CLIENT INFORMATION ---------- ////////////////////

		this._ss.CompanyNumForDuplicate = null;

		window.scrollTo(0, 0);

		this._ss.Betipul = 0;
		this._ss.TeudotDetailsEach = [];
		if (localStorage.getItem('UserName') !== null && localStorage.getItem('AgentId') !== null) {
			this._ss.UserNameConnected = localStorage.getItem('UserName');
			this._ss.AgentIdConnected = localStorage.getItem('AgentId');
		} else {
			this.router.navigate(['login']);
		}

		this.setupBarcodeListener();

		this.setupFormListeners();

		this.getInitialData();

		// console.log('virtualKeyboard' in navigator ? 'keyboard' : 'no keyboard');
	}

	async getInitialData() {
		this._loading.changeLoadingStatuses({ isLoading: true });
		try {
			const products = await this._products.getProductsWithoutAgent('likut');
			if (!products) {
				throw new Error('No products found');
			}
			this.productsFromMemory$.next(products);
		} catch (error) {
			alert('לא ניתן לטעון פריטים, אנא נסה שנית מאוחר יותר');
			this.router.navigate(['home']);
		}

		await this.handleRefreshBarcodes();
		this._loading.changeLoadingStatuses({ isLoading: false });
	}

	async handleRefreshBarcodes() {
		// if (!this._likutScan.barcodeDefinitions$.value?.length || !this._likutScan.barcodeToProducts$.value?.length) {
		await this._likutScan.handleGetBarcodeDefinitions(this.destroy$);
		await this._likutScan.handleGetBarcodeToProducts(this.destroy$);
		// }

		this.products$.next(this._likutScan.barcodeToProducts$.value);
		this.definitions$.next(this._likutScan.barcodeDefinitions$.value);
	}

	closeDrawer() {
		this.isEdittingBarcodeProduct = false;
		this.form.reset();
		this.updateSuccess = false;
		this.form.enable();
		this.showHideFilterPopup(false);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	showHideFilterPopup(value): void {
		this.productBarcodeDrawerOpen = value;
	}

	async handleFormSubmit() {
		this.form.markAllAsTouched();
		if (this.form.invalid) return;

		const { barcodeDefId, idFromBarcode, packQuantity, productID, quantity } = this.form.value;

		const body: LikutProductBarcodePayload = {
			BarcodeDefId: +barcodeDefId,
			BarcodeLen: 0,
			IdFromBarcode: idFromBarcode,
			ProductId: productID,
			QTYDefault: isFinite(+quantity) ? +quantity : null,
			QTYPackDefault: isFinite(+packQuantity) ? +packQuantity : null,
		};

		if (this.isEdittingBarcodeProduct) {
			await this.handlePatchProduct(this.selectedBarcodeProductID, body);
			return;
		}

		try {
			await this.handlePostProduct(body);
		} catch (error) {}
	}

	handlePostProduct(body: LikutProductBarcodePayload) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.postProduct(body).subscribe({
				next: res => {
					this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.updateSuccess = false;
					this._loading.changeLoadingStatuses({ isLoading: false });
					reject();
				},
			});
		});
	}

	async handlePatchProduct(rowID: number, postBody: LikutProductBarcodePayload) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.patchProduct(rowID, postBody).subscribe({
				next: res => {
					this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this.updateSuccess = false;
					this._loading.changeLoadingStatuses({ isLoading: false });
					reject();
				},
			});
		});
	}

	handleDeleteProduct(rowID: number) {
		return new Promise<void>((resolve, reject) => {
			this._likutScan.deleteProduct(rowID).subscribe({
				next: async res => {
					await this.handleAfterHttpRequest(res, resolve, reject);
				},
				error: err => {
					console.log(err);
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					this._loading.changeLoadingStatuses({ isLoading: false });
					this.updateSuccess = false;
					reject();
				},
			});
		});
	}

	openNewBarcodeProductDrawer(formData: Required<typeof this.form.value> = { ...formDefaultValue }) {
		this.showHideFilterPopup(true);

		this.form.setValue(formData);

		if ('virtualKeyboard' in navigator) {
			navigator['virtualKeyboard']?.show();
		}
		setTimeout(() => {
			this.form.updateValueAndValidity();
		}, 0);
	}

	handleEditProductBarcode(product: LikutProductBarcode) {
		this.isEdittingBarcodeProduct = true;
		const values = structuredClone(formDefaultValue);
		const barcodeProduct = structuredClone(product);

		this.selectedBarcodeProductID = barcodeProduct.RowID;

		values.productID = barcodeProduct.ProductId;
		values.barcodeDefId = barcodeProduct.BarcodeDefId;
		values.idFromBarcode = barcodeProduct.IdFromBarcode;
		values.quantity = barcodeProduct.QTYDefault || null;
		values.packQuantity = barcodeProduct.QTYPackDefault || null;

		this.openNewBarcodeProductDrawer({ ...values });
	}

	async handleAfterHttpRequest(res: ResponseFromExpress<LikutProductBarcode>, resolve: (value: void | PromiseLike<void>) => void, reject: (reason?: any) => void) {
		this._loading.changeLoadingStatuses({ isLoading: false });

		if (res === 'ConnectionError' || res === 'RequestError') {
			console.log(res);
			alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
			return reject();
		}

		this.updateSuccess = true;

		this.selectedBarcodeProductID = null;

		this.products$.next(res.recordset);
		this._likutScan.updateStorage({ barcodeToProducts: res.recordset });
		resolve();
	}

	async handleChangeProdcut(e: MatButtonToggleChange | MouseEvent | PointerEvent | Event, rowID: number, action: 'delete') {
		if (this.isLoadingProgressBarShowing) return;

		this.isLoadingProgressBarShowing = true;

		let msg = 'קרתה תקלה בתקשורת נתונים, אנא נסו שנית';

		if (action === 'delete' && !(e instanceof MatButtonToggleChange)) {
			try {
				await this.handleDeleteProduct(rowID);
				msg = 'פריט נמחק בהצלחה';
				this.updateSuccess = false;
			} catch (error) {}
		}

		this._bottomPopup.show(msg, { action: '' });

		this.isLoadingProgressBarShowing = false;
	}

	BackToDohot() {
		history.back();
	}

	displayFnDefinition(definition: LikutBarcodeDefinition) {
		return displayFn({ mode: 'barcodeDefinitions', value: definition });
	}

	logDummyBarcodeForSelectedDefinition() {
		if (this.form.controls.barcodeDefId.invalid) {
			throw new Error('Invalid barcode definition');
		}

		const definition = this.definitions$.value.find(def => def.Id === +this.form.controls.barcodeDefId.value);

		if (!definition) {
			throw new Error('No definition found');
		}

		let product;

		if (this.form.controls.idFromBarcode.value) {
			product = { IdFromBarcode: this.form.controls.idFromBarcode.value };
		}

		console.log(generateDummyBarcodeFromDefinition(definition, product || null));
	}

	setupFormListeners() {
		this.form.controls.productID.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
			if (!value) return;

			value = value.replaceAll(this._barcode.barcodePrefixCharacter, '').replaceAll(this._barcode.barcodeSuffixCharacter, '');

			this.form.controls.productID.setValue(value, { emitEvent: false });
		});
	}

	setupBarcodeListener() {
		this._barcode
			.createBarcodeStream()
			.pipe(takeUntil(this.destroy$))
			.subscribe(barcode => {
				if (this.form.controls.barcodeDefId.invalid) return;

				const definition = this.definitions$.value.find(def => def.Id === +this.form.controls.barcodeDefId.value);

				if (!definition) return;

				if (definition.BarcodeLen !== barcode.length) {
					this._bottomPopup.show(`אורך ברקוד אינו תואם להגדרה`);
					return;
				}

				const idFromBarcode = this._likutScan.barcodeToMakat({ barcode, definition });

				if (!idFromBarcode) return;

				this.form.controls.idFromBarcode.setValue(idFromBarcode);
			});
	}

	validateBarcodeDefinition(formGroup: typeof this.form) {
		const value = formGroup?.controls?.barcodeDefId.value;
		if (!value || !this.definitions$) return null;
		const definition = this.definitions$.value.find(def => def.Id === +value);
		if (!definition) return { invalidBarcodeDefinition: true };
		return null;
	}

	validateBarcodeFromProduct(formGroup: typeof this.form) {
		const value = formGroup?.controls?.idFromBarcode.value;
		if (!value || formGroup.controls.barcodeDefId.invalid || !this.definitions$) return null;
		const definition = this.definitions$.value.find(def => def.Id === +formGroup.controls.barcodeDefId.value);
		if (!definition) return { invalidBarcodeDefinition: true };
		const idFromBarcode = this._likutScan.barcodeToMakat({ barcode: value.padStart(definition.MakatLoc + definition.MakatLen - 1, '0'), definition });
		if (!idFromBarcode || idFromBarcode !== formGroup.controls.idFromBarcode.value) return { invalidBarcode: true };
		return null;
	}

	validateDuplicateProduct(formGroup: typeof this.form) {
		// if (this.isEdittingBarcodeProduct) return null;
		const { productID, barcodeDefId, idFromBarcode } = formGroup.value;
		if (!productID || !barcodeDefId || !idFromBarcode) return null;
		let products = this.products$.value;
		if (this.isEdittingBarcodeProduct) {
			products = products.filter(product => product.RowID !== this.selectedBarcodeProductID);
		}
		const existingDefinition = products.find(product => product.ProductId === productID && product.BarcodeDefId === +barcodeDefId && product.IdFromBarcode === idFromBarcode);
		return existingDefinition ? { duplicateProduct: true } : null;
	}
}
